/**
 * The one place a connection phase changes.
 *
 * Every surface that shows a connection shows the same four words, so the words are computed here
 * from signals rather than each surface inventing a fifth state out of whatever it happens to
 * observe. Pure, so the transitions are testable without a network.
 *
 * `failed` and `closed` are terminal. A session that has failed is reopened by opening a new one,
 * not by nudging this machine, which is what keeps a dead room from quietly claiming to be live.
 */

import { netErrorMessage } from '@/net/session/sessionErrors';
import type { NetError } from '@/net/session/sessionErrors';
import type { ConnectionPhase, ConnectionStatus } from '@/net/transportContract';

export type LifecycleSignal =
  | { readonly kind: 'joinStarted' }
  /** One signaling strategy was tried. Carries what to show while the next one is dialled. */
  | { readonly kind: 'strategyAttempt'; readonly strategyId: string; readonly detail: string }
  | { readonly kind: 'roomSettled'; readonly strategyId: string }
  | { readonly kind: 'peerReady'; readonly peerCount: number }
  | { readonly kind: 'peerLost'; readonly peerCount: number; readonly wasHost: boolean }
  /** A peer connection went quiet and may yet recover. */
  | { readonly kind: 'peerUnstable' }
  | { readonly kind: 'peerStable' }
  | { readonly kind: 'fatal'; readonly error: NetError }
  | { readonly kind: 'closed' };

const TERMINAL: ReadonlySet<ConnectionPhase> = new Set<ConnectionPhase>(['failed', 'closed']);

export const IDLE_STATUS: ConnectionStatus = {
  phase: 'idle',
  detail: 'Not connected.',
  code: null,
  strategyId: null,
};

/** Applies one signal. Returns the same object when nothing changed, so a listener can skip. */
export function nextStatus(current: ConnectionStatus, signal: LifecycleSignal): ConnectionStatus {
  if (TERMINAL.has(current.phase) && signal.kind !== 'closed') return current;

  switch (signal.kind) {
    case 'joinStarted':
      return {
        phase: 'connecting',
        detail: 'Reaching a signaling relay.',
        code: null,
        strategyId: null,
      };

    case 'strategyAttempt':
      return {
        ...current,
        phase: current.phase === 'connected' ? 'reconnecting' : 'connecting',
        detail: signal.detail,
        strategyId: signal.strategyId,
      };

    case 'roomSettled':
      return {
        phase: 'connected',
        detail: 'Connected.',
        code: null,
        strategyId: signal.strategyId,
      };

    case 'peerReady':
      return { ...current, phase: 'connected', detail: playerCount(signal.peerCount), code: null };

    case 'peerLost':
      return signal.wasHost
        ? { ...current, phase: 'failed', detail: netErrorMessage('hostLeft'), code: 'hostLeft' }
        : { ...current, detail: playerCount(signal.peerCount) };

    case 'peerUnstable':
      return { ...current, phase: 'reconnecting', detail: 'Lost contact, waiting for the player.' };

    case 'peerStable':
      return { ...current, phase: 'connected', detail: 'Connected.', code: null };

    case 'fatal':
      return {
        ...current,
        phase: 'failed',
        detail: signal.error.message,
        code: signal.error.code,
      };

    case 'closed':
      return { ...current, phase: 'closed', detail: 'Session closed.', code: null };
  }
}

function playerCount(peerCount: number): string {
  const total = peerCount + 1;
  return total === 1 ? 'Waiting for players.' : `Connected, ${String(total)} players.`;
}
